<template>
  <q-dialog
    :model-value="transactions_selected_count > 0"
    seamless
    position="bottom"
  >
    <q-card
      style="min-width: fit-content; background-color: white !important;"
      class="bottom-context-card"
      :class="{ 'has-primary-action': bulk_edit_single_action }"
    >
      <q-card-section class="row items-center no-wrap q-px-xl q-py-lg">
        <div>
          <h4 class="text-weight-bold q-my-none q-pr-md bulk-edit-selection-count">
            {{ transactions_all_pages_selected ? transactions_count : transactions_selected_count }}
          </h4>
        </div>

        <div class="q-pr-md">
          <div
            v-if="transactions_selected_count < 2"
            class="text-weight-bold"
          >
            {{ i18n.bulk_edit_selected_one_label }}
          </div>
          <div
            v-else
            class="text-weight-bold"
          >
            {{ i18n.bulk_edit_selected_many_label }}
          </div>
          <div class="text-grey">
            {{ i18n.bulk_edit_caption }}
          </div>
        </div>

        <q-space />

        <q-btn-dropdown
          rounded
          :label="i18n.bulk_edit_categorize_label"
          unelevated
          no-caps
          outline
          class="q-mx-sm"
          content-class="transactions_kind_selector_popup"
        >
          <div class="transactions_kind_selector_panel">
            <div class="transactions_kind_selector_content">
              <div class="transactions_kind_selector_header q-pt-none q-px-none">
                <q-input
                  :model-value="bulk_edit_search"
                  outlined
                  dense
                  :placeholder="i18n.bulk_edit_categories_search_placeholder"
                  autofocus
                  @update:model-value="$emit('update:bulk_edit_search', $event)"
                >
                  <template v-slot:prepend>
                    <q-icon name="search" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      v-if="bulk_edit_search"
                      name="close"
                      class="cursor-pointer"
                      @click="$emit('update:bulk_edit_search', '')"
                    />
                  </template>
                </q-input>
              </div>
              <div class="transactions_kind_selector_scroll">
                <q-list dense>
                  <!-- History -->
                  <template v-if="recentTransactionKinds.length > 0">
                    <q-item-label
                      header
                      class="text-bold"
                    >
                      <q-avatar
                        color="black"
                        size="28px"
                        class="q-mr-sm"
                      >
                        <q-icon
                          name="las la-history"
                          color="white"
                          size="18px"
                          class=""
                        />
                      </q-avatar>
                      {{ i18n.bulk_edit_categories_recent }}
                    </q-item-label>

                    <q-item
                      v-for="(transactionKind, index) in recentTransactionKinds"
                      :key="transactionKind.id + 'recent'"
                      v-close-popup
                      clickable
                      @click="$emit('bulk_edit_kind_select', transactionKind)"
                    >
                      <q-item-section>
                        <q-item-label>{{ transactionKind.label }}</q-item-label>
                      </q-item-section>

                      <q-item-section
                        v-if="index == 0"
                        side
                      >
                        <div>
                          <code class="text-dark">SHIFT</code>
                          +
                          <code class="text-dark">C</code>
                        </div>
                        <!-- Display SHIFT C shortcut -->
                      </q-item-section>
                    </q-item>

                    <q-separator />
                  </template>

                  <template
                    v-for="category in bulk_edit_categories"
                    :key="category.id"
                  >
                    <q-item-label
                      header
                      class="text-bold"
                    >
                      <q-avatar
                        :color="category.icon.color"
                        size="28px"
                        class="q-mr-sm"
                      >
                        <q-icon
                          :name="category.icon.class"
                          color="black"
                          size="18px"
                          class=""
                        />
                      </q-avatar>
                      {{ category.label }}
                    </q-item-label>

                    <template v-for="kind in category.transaction_kinds">
                      <q-item
                        v-if="
                          kind.name != 'trade_in' &&
                            kind.name != 'trade_out'
                        "
                        :key="kind.id"
                        v-close-popup
                        clickable
                        @click="$emit('bulk_edit_kind_select', kind)"
                      >
                        <q-item-section>
                          <q-item-label>
                            {{ kind.label }}
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>

                    <q-separator />
                  </template>

                  <!-- Add uncategorize option button -->
                  <q-item
                    v-close-popup
                    clickable
                    class=""
                    @click="$emit('bulk_edit_kind_select', 'uncategorized')"
                  >
                    <q-item-section class="">
                      <q-item-label>
                        {{ i18n.bulk_edit_uncategorize_action }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </div>
          </div>
        </q-btn-dropdown>

        <!-- Single action button for opposite sign transactions -->
        <q-btn
          v-if="bulk_edit_single_action"
          :label="bulk_edit_single_action.label"
          unelevated
          no-caps
          rounded
          color="primary"
          class="q-mx-sm bulk-edit-primary-action"
          :loading="bulk_edit_action_loading"
          @click="handleSingleAction"
        >
          <q-tooltip
            v-if="bulk_edit_action_description"
            anchor="center right"
            self="center left"
            :delay="500"
            class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
          >
            {{ bulk_edit_action_description }}
          </q-tooltip>
        </q-btn>

        <!-- Delete All -->
        <q-btn
          unelevated
          color="negative"
          flat
          round
          size="sm"
          icon="las la-trash-alt"
          @click="$emit('bulk_edit_delete_confirm')"
        >
          <q-tooltip
            anchor="center right"
            self="center left"
            class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
            :delay="300"
          >
            {{ i18n.bulk_edit_delete_action }}
          </q-tooltip>
        </q-btn>

        <template v-if="transactions_selected_sums?.show">
          <q-space />

          <q-item>
            <q-item-section>
              <q-item-label class="text-bold">
                {{ i18n.bulk_edit_transactions_sum }}
              </q-item-label>
              <q-item-label caption>
                <q-btn-dropdown
                  dense
                  padding="0px"
                  split
                  flat
                  push
                  no-caps
                  class="multi-asset-totals-btn"
                >
                  <template v-slot:label>
                    <!-- Display the first asset's total directly on the button -->
                    <AssetChip
                      :amount="parseFloat(transactions_selected_sums.items[0].total)"
                      :amount_formatted="transactions_selected_sums.items[0].total_formatted"
                      :asset_icon="transactions_selected_sums.items[0].asset_icon"
                      :symbol="transactions_selected_sums.items[0].asset_symbol"
                      :exchange_symbol="transactions_selected_sums.items[0].asset_symbol"
                      size="13"
                      no_padding
                      no_margin
                    />
                  </template>

                  <q-list
                    style="min-width: 240px"
                    class="q-pa-sm"
                  >
                    <!-- Total fiat value -->
                    <q-item class="q-px-lg">
                      <q-item-section>
                        <q-item-label caption>
                          {{ i18n.transactions_sums_title }}
                        </q-item-label>
                        <q-item-label class="text-bold">
                          {{ transactions_selected_sums.fiat_amount_total_formatted }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <!-- Loop over all items except the first one to display each asset's total in the dropdown -->
                    <q-item
                      v-for="(item, index) in transactions_selected_sums.items"
                      :key="`item-${index}`"
                      v-close-popup
                    >
                      <q-item-section>
                        <AssetChip
                          :amount="parseFloat(item.total)"
                          :amount_formatted="item.total_formatted"
                          :asset_icon="item.asset_icon"
                          :symbol="item.asset_symbol"
                          :exchange_symbol="item.asset_symbol"
                          size="13"
                        />
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>

        <div>
          <q-btn
            icon="close"
            unelevated
            flat
            round
            dense
            @click="$emit('bulk_edit_close')"
          />
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import AssetChip from '@/pages/w/transactions/asset_chip.vue';

export default {
  name: 'TransactionsBulkEdit',
  components: {
    AssetChip,
  },
  props: {
    i18n: { type: Object, required: true },
    transactions_selected_count: { type: Number, required: true },
    transactions_all_pages_selected: { type: Boolean, required: true },
    transactions_count: { type: Number, required: true },
    bulk_edit_search: { type: String, required: false, default: '' },
    recentTransactionKinds: { type: Array, required: true },
    bulk_edit_categories: { type: Array, required: true },
    bulk_edit_single_action: { type: Object, required: false, default: null },
    bulk_edit_action_description: { type: String, required: false, default: null },
    bulk_edit_action_loading: { type: Boolean, required: true },
    transactions_selected_sums: { type: Object, required: true },
  },
  emits: [
    'update:bulk_edit_search',
    'bulk_edit_close',
    'bulk_edit_kind_select',
    'bulk_edit_transactions_merge',
    'bulk_edit_transactions_internal_transfer',
    'bulk_edit_transactions_bridge',
    'bulk_edit_delete_confirm',
  ],
  methods: {
    handleSingleAction() {
      if (!this.bulk_edit_single_action) return;

      switch (this.bulk_edit_single_action.action) {
        case 'merge':
          this.$emit('bulk_edit_transactions_merge');
          break;
        case 'internal_transfer':
          this.$emit('bulk_edit_transactions_internal_transfer');
          break;
        case 'bridge':
          this.$emit('bulk_edit_transactions_bridge');
          break;
      }
    },
  },
};
</script>

<style scoped>
.bulk-edit-selection-count {
  font-size: 1.75rem;
  line-height: 1.2;
  padding: 4px 12px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  margin-right: 12px;
}

.body--dark .bulk-edit-selection-count {
  background-color: rgba(255, 255, 255, 0.08);
}

.bulk-edit-primary-action {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: subtle-pulse 2s ease-in-out infinite;
}

@keyframes subtle-pulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  50% {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  }
}

.body--dark .bulk-edit-primary-action {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.body--dark .bulk-edit-primary-action {
  animation: subtle-pulse-dark 2s ease-in-out infinite;
}

@keyframes subtle-pulse-dark {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  }
}

/* Make the card more visible */
.bottom-context-card {
  background-color: #ffffff !important;
  background: #ffffff !important;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15) !important;
  min-height: 80px;
  opacity: 1 !important;
}

.body--dark .bottom-context-card {
  background-color: #1e1e1e !important;
  background: #1e1e1e !important;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4) !important;
}

/* Highlight the card when a primary action is available */
.bottom-context-card.has-primary-action {
  background: linear-gradient(to top, rgba(25, 118, 210, 0.15), #ffffff) !important;
  border-top: 4px solid #1976d2 !important;
  box-shadow: 0 -6px 24px rgba(25, 118, 210, 0.2) !important;
}

.body--dark .bottom-context-card.has-primary-action {
  background: linear-gradient(to top, rgba(144, 202, 249, 0.2), #1e1e1e) !important;
  border-top: 4px solid #90caf9 !important;
  box-shadow: 0 -6px 24px rgba(144, 202, 249, 0.3) !important;
}
</style>
